import { CardType } from '~types'
import styles from './Card.module.scss'
import cx from 'classnames'
import { NEXT_IMAGE_QUALITY } from '~constants'
import Image from 'next/image'
import { ButtonGroup, Video } from '~elements'
import { Eyebrow, Heading, Paragraph } from '~elements'
import { getBackgroundGradient } from 'utils/styles/getBackgroundGradient'
import { useWindowSize } from 'utils/hooks'
import Quotes from '/public/assets/svg/quotes.svg'

const Card = ({
  backgroundColor,
  backgroundGradient,
  buttonGroup,
  centerCopyOnMobile,
  description,
  eyebrow,
  hasDescriptionListCheckMark,
  hasLargeFontSizes,
  hasOverrideStyles,
  hasVideo,
  headline,
  image,
  mediaPosition,
  overrideBackgroundColor,
  overrideSubheadlineColor,
  readMore,
  stackMediaAndCopy = true,
  subheadline,
  subheadlineColor = 'light',
  video,
  overrideContainerClass,
  isTestimonial = false,
  testimonial,
  overrideBackgroundStyle
}: CardType) => {
  const { isBreakpoint: isBelowMd } = useWindowSize('sm', '<')
  const { isBreakpoint: isBelowLg } = useWindowSize('lg', '<')
  const linkModule = 'card'
  const isMediaRight = !stackMediaAndCopy && mediaPosition === 'right'
  const hasNoBackgroundStyle = backgroundColor === 'transparent' && backgroundGradient === 'transparent'

  // Determine gradient start position based on media position if not explicitly set
  const resolvedGradientStartPosition = isMediaRight ? 'right' : 'left'

  const backgroundStyle =
    overrideBackgroundStyle ||
    getBackgroundGradient({
      gradient: backgroundGradient,
      color: backgroundColor,
      overrideColor: hasOverrideStyles ? overrideBackgroundColor : null,
      direction: stackMediaAndCopy || isBelowMd ? 'vertical' : 'horizontal',
      gradientStartPosition: resolvedGradientStartPosition
    })
  const containerClass = cx(
    styles['card__container'],
    styles[!stackMediaAndCopy && !isBelowMd ? 'card__container--horizontal' : 'card__container--vertical'],
    isMediaRight && !isBelowMd && styles['card__container--horizontal-reverse'],
    overrideContainerClass && overrideContainerClass
  )
  const noMedia = !image && !video
  return (
    <div className={containerClass}>
      {noMedia ? null : (
        <div className={styles['card__media-wrapper']}>
          {eyebrow && <Eyebrow {...eyebrow} />}
          {!hasNoBackgroundStyle && <div className={styles['card__media-wrapper-gradient']} />}
          {hasVideo && video ? (
            <Video backgroundVideoXl={video} posterImageXl={image} autoPlay={true} videoLoop={true} />
          ) : (
            image && (
              <Image
                src={image?.url}
                alt={image?.altText ?? headline ?? ''}
                sizes='100vw'
                fill
                width={0}
                height={0}
                style={{
                  objectFit: hasNoBackgroundStyle || stackMediaAndCopy ? 'contain' : 'cover',
                  ...(hasNoBackgroundStyle && { objectPosition: 'top center' })
                }}
                quality={NEXT_IMAGE_QUALITY}
              />
            )
          )}
        </div>
      )}
      <div
        className={cx(
          styles['card__headline-wrapper'],
          centerCopyOnMobile && isBelowMd && styles['card__headline-wrapper-mobile-centered'],
          hasDescriptionListCheckMark && styles['card__headline-wrapper-check-mark-list'],
          hasLargeFontSizes && styles['card__headline-wrapper--has-large-font-sizes'],
          hasNoBackgroundStyle && styles['card__headline-wrapper--has-no-background-style'],
          noMedia && styles['--no-media'],
          isTestimonial && styles['--is-testimonial']
        )}
        {...(backgroundStyle && { style: backgroundStyle })}
      >
        <div className={styles['card__headline-inner']}>
          {headline && (
            <Heading level={3} justify={isBelowMd && centerCopyOnMobile ? 'center' : 'left'} noMarginTop>
              {headline}
            </Heading>
          )}
          {subheadline && (
            <Heading
              level={5}
              levelDisplay='eyebrow'
              color={hasOverrideStyles ? overrideSubheadlineColor : subheadlineColor}
              justify={isBelowMd && centerCopyOnMobile ? 'center' : 'left'}
              noMarginTop
            >
              {subheadline}
            </Heading>
          )}

          {isTestimonial
            ? null
            : description?.html && (
                <div className={styles['card__description']}>
                  <Paragraph
                    text={description}
                    justify={isBelowMd && centerCopyOnMobile ? 'center' : 'left'}
                    readMoreText={hasNoBackgroundStyle && readMore ? readMore : null}
                    fontSize={isBelowMd || !hasLargeFontSizes ? 'md' : 'md-plus'}
                  />
                </div>
              )}

          {isTestimonial && testimonial && (
            <div className={styles['card__testimonial']}>
              {testimonial.hasQuoteSymbol && (
                <Quotes
                  className={styles['card__testimonial-quote-icon']}
                  aria-hidden='true'
                  preserveAspectRatio='none'
                />
              )}
              {testimonial.someQuoteSymbol && !testimonial.hasQuoteSymbol && (
                <div className={styles['card__testimonial-quote-icon']}></div>
              )}
              {description?.html && (
                <div className={styles['card__testimonial-description']}>
                  <Paragraph text={description} justify={'left'} />
                </div>
              )}
              <div className={styles['card__testimonial-bottom-section']}>
                <div className={styles['card__testimonial-attribution']}>
                  {testimonial.attributionImage && (
                    <div className={styles['card__testimonial-attribution-image']}>
                      <Image
                        src={testimonial.attributionImage.url}
                        alt={testimonial.attributionTitle || 'Attribution'}
                        width={isBelowMd ? 50 : isBelowLg ? 40 : 50}
                        height={isBelowMd ? 50 : isBelowLg ? 40 : 50}
                        style={{ borderRadius: '50%', objectFit: 'cover' }}
                      />
                    </div>
                  )}
                  <div>
                    <div className={styles['card__testimonial-attribution-text']}>
                      {testimonial.attributionTitle && (
                        <p className={styles['card__testimonial-attribution-title']}>{testimonial.attributionTitle}</p>
                      )}
                      {testimonial.attributionDescription && (
                        <p className={styles['card__testimonial-attribution-description']}>
                          {testimonial.attributionDescription}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {testimonial.companyLogo && (
                  <div className={styles['card__testimonial-company-logo']}>
                    <Image
                      src={testimonial.companyLogo.url}
                      alt={testimonial.companyLogo.altText || ''}
                      width={isBelowLg ? 98 : 163}
                      height={isBelowLg ? 15 : 25}
                      style={{ objectFit: 'contain' }}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {buttonGroup && (
            <div className={styles['cta-container']}>
              <ButtonGroup
                {...buttonGroup}
                layout={buttonGroup?.layout || (isBelowMd ? 'stacked' : 'inline')}
                analytics={{
                  linkModule: linkModule,
                  parentHeadline: headline
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Card
